import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent, MouseEvent, ReactNode } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, FARM_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { formatMoney } from "@/lib/format";
import type { FarmOverview, Guid, PartnerSplit } from "@/api/types";
import "./Farm.css";

type OverviewStatus = "loading" | "ready" | "error";

/** ef-061-8 — the header's "Equipment" link: a real `<a href>` (a modified click keeps working) that a plain
 *  left click turns into client-side navigation, the same `pushState` + `popstate` dispatch `OverviewCard` uses. */
function goToEquipment(event: MouseEvent<HTMLAnchorElement>): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  window.history.pushState({}, "", "/farm/equipment");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * ef-046-5 — the Farm tile's Overview tab, `/farm`: four cards (Fields, Livestock, Grain
 * storage, Grain contracts), each a real server-side aggregate
 * (`GET /api/demo/farm/overview` — `FarmOverviewEndpoints.cs`) linking to its own tab.
 * Replaces the honest empty state ef-037a shipped (this file's own prior header) now that
 * there is real content behind three of the Farm tile's tabs.
 *
 * NO `onNavigate` PROP — this job's own file scope names only `Farm.tsx` + `Farm.css`
 * (no `App.tsx`), so a card's link drives the browser's own History API directly, the
 * exact `pushState` + manual `popstate` dispatch `AreaTabs.tsx`'s own `go()` already
 * uses — its own remarks explain why that needs no new plumbing threaded through App.tsx:
 * the `popstate` listener already there for the browser's back/forward buttons is the one
 * place a route change is observed.
 *
 * EVERY FIGURE EITHER RENDERS OR EXPLAINS ITS OWN ABSENCE (ADR 0009) — a card whose
 * figure is zero shows its own empty state instead of a bare "0", same "empty state per
 * card" contract `farm-overview.spec.ts`'s own header names. Only `openContractBalance`
 * is money (ADR 0009 §5): acres/head/bushels use the ordinary text face, never the
 * numeric/mono face reserved for currency.
 */
export function Farm() {
  const [overview, setOverview] = useState<FarmOverview | null>(null);
  const [status, setStatus] = useState<OverviewStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setStatus("loading");
    setError(null);
    try {
      setOverview(await demoApi.getFarmOverview());
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load the farm overview."));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main className="farm-page">
      <PageHeader
        title="Farm"
        subtitle="Fields, livestock, milk and grain — the modules still on the roadmap."
        action={<a className="btn btn--ghost" href="/farm/equipment" onClick={goToEquipment}>Equipment</a>}
      />
      <AreaTabs area="/farm" tabs={FARM_TABS} label="Farm" />

      {status === "loading" && overview === null ? (
        <StateMessage tone="loading" title="Loading the farm overview…" />
      ) : status === "error" ? (
        <StateMessage
          tone="error"
          title="Could not load the farm overview."
          detail={error ?? undefined}
        />
      ) : overview ? (
        <div className="farm-overview">
          <OverviewCard
            title="Fields"
            href="/farm/fields"
            empty={overview.acresInCrop <= 0}
            emptyDetail="Nothing planted for this year yet."
          >
            <p className="farm-overview__figure">{overview.acresInCrop.toFixed(2)} ac in crop</p>
          </OverviewCard>

          <OverviewCard
            title="Livestock"
            href="/farm/livestock"
            empty={overview.headCount <= 0}
            emptyDetail="No animals on file yet."
          >
            <p className="farm-overview__figure">{overview.headCount} head</p>
          </OverviewCard>

          <OverviewCard
            title="Grain storage"
            href="/farm/grain"
            empty={overview.bushelsOnHand.length === 0}
            emptyDetail="Nothing in the bins yet."
          >
            <ul className="farm-overview__list">
              {overview.bushelsOnHand.map((bushels) => (
                <li key={bushels.crop} className="farm-overview__list-row">
                  <span>{bushels.crop}</span>
                  <span>{bushels.qty.toFixed(2)} bu</span>
                </li>
              ))}
            </ul>
          </OverviewCard>

          <OverviewCard
            title="Grain contracts"
            href="/farm/grain"
            empty={overview.openContractBalance <= 0}
            emptyDetail="No open contracts."
          >
            <p className="farm-overview__figure farm-overview__figure--money">
              {formatMoney(overview.openContractBalance)}
            </p>
          </OverviewCard>

          <OverviewCard
            title="Milk"
            href="/farm/milk"
            empty={overview.milkByUnit.length === 0}
            emptyDetail="No tickets recorded yet."
          >
            <ul className="farm-overview__list">
              {overview.milkByUnit.map((milk) => (
                <li key={milk.unit} className="farm-overview__list-row">
                  <span>{milk.unit}</span>
                  <span>{milk.qty.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </OverviewCard>
        </div>
      ) : null}

      <PartnersSection />
    </main>
  );
}

interface OverviewCardProps {
  title: string;
  href: string;
  empty: boolean;
  emptyDetail: string;
  children: ReactNode;
}

/** Real navigation, not a fake button (rules/ui-charters — "no fake buttons, fewer
 *  buttons"): a genuine `<a href>` that a modified click (new tab, middle-click) keeps
 *  working exactly like any other link, same modifier-key guard `AppShell.tsx`'s own
 *  `handleNavClick` uses. Only a plain left click is intercepted for client-side
 *  navigation, via the same `pushState` + `popstate` dispatch this file's own header
 *  explains. */
function OverviewCard({ title, href, empty, emptyDetail, children }: OverviewCardProps) {
  function onClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  return (
    <div className="farm-overview__card" role="group" aria-label={title}>
      <span className="farm-overview__card-title">{title}</span>
      {empty ? (
        <p className="farm-overview__card-empty">{emptyDetail}</p>
      ) : (
        children
      )}
      <a className="farm-overview__card-link" href={href} onClick={onClick}>
        View {title.toLowerCase()}
      </a>
    </div>
  );
}

type PartnersStatus = "loading" | "ready" | "error";

/**
 * ef-050-2 — the Overview tab's "Partners" section (ADR 0009: no sixth Farm tab), CRUD
 * for one entity's `partner_split` rows (`PartnerSplitEndpoints.cs`/
 * `PartnerSplitContracts.cs`). NO SEPARATE COMPONENT FILE — this job's own file scope
 * names only `Farm.tsx`/`Farm.css`, same posture `Milk.tsx`'s own local dialog takes for
 * its own scope.
 *
 * ONE SHARED FORM FOR ADD AND EDIT — same reasoning `Places.tsx`'s own header gives for
 * its identical shape: a farmer editing one partner's percentage does not need a
 * different screen from the one that added it. Percentage is a COUNT, never money (ADR
 * 0009 §5) — the ordinary text face, never `--font-family-numeric`.
 *
 * ENTITY IS RESOLVED IMPLICITLY — the same shared, persisted switcher
 * (`useSelectedEntity`) every other Farm screen reads, never a picker inside this
 * section's own form.
 *
 * NO ARCHIVED DISCLOSURE, NO CONFIRM DIALOG ON ARCHIVE — `PartnerSplitEndpoints.cs` ships
 * `.../restore` this wave, but nothing in this slice's own e2e contract exercises an
 * archived list here; the API remains reachable for a later screen without one. A
 * partner split carries no downstream history the way an entity/place does (0075's own
 * header: no posting integration at all this wave), so `Places.tsx`'s own alertdialog
 * would be friction without a real guard behind it — one click archives, same "fewer
 * buttons" posture (rules/ui-charters) `MilkTicketEndpoints.cs`'s own create/list-only
 * scope takes for its sibling table.
 */
function PartnersSection() {
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [splits, setSplits] = useState<PartnerSplit[] | null>(null);
  const [status, setStatus] = useState<PartnersStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const [partnerName, setPartnerName] = useState("");
  const [percentage, setPercentage] = useState("");
  const [editingId, setEditingId] = useState<Guid | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [archivingId, setArchivingId] = useState<Guid | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const nameId = useId();
  const percentageId = useId();

  /** Re-reads the whole split list — same "wholesale reload after every write, never
   *  patch arrays in place" convention `Milk.tsx`'s own `load()` documents for itself. */
  const load = useCallback(async (): Promise<void> => {
    if (!entityId) return;
    setStatus("loading");
    setError(null);
    try {
      setSplits(await demoApi.listPartnerSplits(entityId));
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load partner splits."));
      setStatus("error");
    }
  }, [entityId]);

  useEffect(() => {
    if (entityStatus === "loading") return;
    if (!entityId) {
      setSplits([]);
      setStatus("ready");
      return;
    }
    void load();
  }, [load, entityId, entityStatus]);

  function cancelEdit(): void {
    setEditingId(null);
    setPartnerName("");
    setPercentage("");
    setFormError(null);
    setFieldErrors(null);
  }

  function startEdit(split: PartnerSplit): void {
    setEditingId(split.id);
    setPartnerName(split.partnerName);
    setPercentage(String(split.percentage));
    setFormError(null);
    setFieldErrors(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    if (!entityId) {
      setFormError("No farm entity found for this tenant yet.");
      return;
    }

    const trimmedName = partnerName.trim();
    const pct = percentage.trim() === "" ? NaN : Number(percentage);

    /* @ui-check: client-side ONLY — PartnerSplitEndpoints.cs's own ValidatePartnerName/
       ValidatePercentage still run server-side and remain the actual contract. This
       guard exists so a blank/unparseable field never reaches the network as a silently
       wrong value (`Number("")` is `0`, not `NaN`). */
    const missing: Record<string, string[]> = {};
    if (trimmedName === "") missing.partnerName = ["A partner name is required."];
    if (Number.isNaN(pct)) missing.percentage = ["Enter a percentage."];
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await demoApi.updatePartnerSplit(editingId, { partnerName: trimmedName, percentage: pct });
      } else {
        await demoApi.createPartnerSplit({ entityId, partnerName: trimmedName, percentage: pct });
      }
      cancelEdit();
      await load();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this partner split."));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(split: PartnerSplit): Promise<void> {
    setArchivingId(split.id);
    setArchiveError(null);
    try {
      await demoApi.archivePartnerSplit(split.id);
      if (editingId === split.id) cancelEdit();
      await load();
    } catch (err) {
      setArchiveError(problemMessage(err, "Could not archive this partner."));
    } finally {
      setArchivingId(null);
    }
  }

  const rows = splits ?? [];

  return (
    <section
      className="farm-partners"
      aria-labelledby="farm-partners-heading"
      data-state={status === "ready" && rows.length === 0 ? "empty" : status}
    >
      <h2 id="farm-partners-heading" className="farm-partners__title">
        Partners
      </h2>

      <form className="farm-partners__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="farm-partners__field">
          <label htmlFor={nameId}>Partner name</label>
          <input
            id={nameId}
            type="text"
            maxLength={120}
            value={partnerName}
            onChange={(e) => setPartnerName(e.target.value)}
            aria-invalid={fieldErrors?.partnerName ? true : undefined}
          />
        </span>

        <span className="farm-partners__field farm-partners__field--pct">
          <label htmlFor={percentageId}>Percentage</label>
          <input
            id={percentageId}
            type="number"
            min={0.01}
            max={100}
            step={0.01}
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            aria-invalid={fieldErrors?.percentage ? true : undefined}
          />
        </span>

        <span className="farm-partners__form-actions">
          <button type="submit" className="btn btn--primary" disabled={saving || !entityId}>
            {saving ? "Saving…" : editingId ? "Save" : "Add partner"}
          </button>
          {editingId && (
            <button type="button" className="btn btn--ghost" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          )}
        </span>

        {formError && (
          <p className="farm-partners__error" role="alert">
            {formError}
          </p>
        )}
      </form>

      {status === "loading" && <StateMessage tone="loading" title="Loading partners…" />}

      {status === "error" && (
        <StateMessage tone="error" title="Could not load partners." detail={error ?? undefined} />
      )}

      {status === "ready" && rows.length === 0 && (
        <StateMessage
          tone="empty"
          title="No partners yet."
          detail="Add a partner to split this entity's proceeds."
        />
      )}

      {status === "ready" && rows.length > 0 && (
        <table className="farm-partners__table ruled-rows">
          <caption className="visually-hidden">Partner splits for the selected entity</caption>
          <thead>
            <tr>
              <th scope="col">Partner</th>
              <th scope="col">Percentage</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((split) => (
              <tr key={split.id}>
                <td>{split.partnerName}</td>
                <td className="farm-partners__num">{split.percentage}%</td>
                <td className="farm-partners__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => startEdit(split)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void handleArchive(split)}
                    disabled={archivingId === split.id}
                  >
                    {archivingId === split.id ? "…" : "Archive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {archiveError && (
        <p className="farm-partners__error" role="alert">
          {archiveError}
        </p>
      )}
    </section>
  );
}
